import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface MicroInteractionProps extends HTMLMotionProps<'div'> {
  children: React.ReactNode;
  type?: 'bounce' | 'scale' | 'shake' | 'glow' | 'press';
  disabled?: boolean;
}

export function MicroInteraction({ 
  children, 
  type = 'scale', 
  disabled = false,
  ...props 
}: MicroInteractionProps) {
  const variants = {
    bounce: {
      whileHover: { y: -4 },
      whileTap: { y: 0, scale: 0.98 },
    },
    scale: {
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.98 },
    },
    shake: {
      whileHover: { x: [0, -2, 2, -2, 2, 0] },
      whileTap: { scale: 0.98 },
    },
    glow: {
      whileHover: { 
        boxShadow: '0 0 20px hsl(var(--primary) / 0.4)',
        scale: 1.01,
      },
      whileTap: { scale: 0.99 },
    },
    press: {
      whileHover: { scale: 1.01 },
      whileTap: { scale: 0.95 },
    },
  };

  const selectedVariant = variants[type];

  if (disabled) {
    return <div {...(props as any)}>{children}</div>;
  }

  return (
    <motion.div
      whileHover={selectedVariant.whileHover}
      whileTap={selectedVariant.whileTap}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// Success checkmark animation
export function SuccessCheck({ size = 24 }: { size?: number }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      initial="hidden"
      animate="visible"
    >
      <motion.circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2"
        fill="hsl(var(--accent) / 0.2)"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: 'backOut' }}
      />
      <motion.path
        d="M8 12l2.5 2.5L16 9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
      />
    </motion.svg>
  );
}

// Animated counter for numbers
export function AnimatedCounter({ 
  value, 
  duration = 1,
  className = '' 
}: { 
  value: number; 
  duration?: number;
  className?: string;
}) {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    const start = displayValue;
    const end = value;
    const startTime = Date.now();
    const durationMs = duration * 1000;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      
      setDisplayValue(Math.round(start + (end - start) * eased));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <motion.span
      key={value}
      initial={{ scale: 1.2, color: 'hsl(var(--accent))' }}
      animate={{ scale: 1, color: 'currentColor' }}
      className={className}
    >
      {displayValue}
    </motion.span>
  );
}

// Pulse dot indicator
export function PulseDot({ 
  color = 'hsl(var(--accent))',
  size = 8 
}: { 
  color?: string;
  size?: number;
}) {
  return (
    <span className="relative inline-flex">
      <motion.span
        animate={{ scale: [1, 1.5, 1], opacity: [0.7, 0, 0.7] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute inline-flex h-full w-full rounded-full"
        style={{ backgroundColor: color, width: size, height: size }}
      />
      <span 
        className="relative inline-flex rounded-full"
        style={{ backgroundColor: color, width: size, height: size }}
      />
    </span>
  );
}

// Staggered list animation wrapper
export function StaggeredList({ 
  children, 
  staggerDelay = 0.1 
}: { 
  children: React.ReactNode;
  staggerDelay?: number;
}) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
    >
      {React.Children.map(children, (child) => (
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}

// Celebration burst effect
export function CelebrationBurst({ 
  trigger, 
  colors = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--secondary))', 'hsl(var(--soft-accent))'] 
}: { 
  trigger: boolean;
  colors?: string[];
}) {
  if (!trigger) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            scale: 0,
            x: 0,
            y: 0,
            opacity: 1,
          }}
          animate={{ 
            scale: [0, 1],
            x: Math.cos((i * 30) * Math.PI / 180) * 100,
            y: Math.sin((i * 30) * Math.PI / 180) * 100,
            opacity: [1, 0],
          }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full"
          style={{ backgroundColor: colors[i % colors.length] }}
        />
      ))}
    </div>
  );
}
